import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Box,
  Menu,
  MenuItem,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Backdrop,
  CircularProgress,
  FormControl,
  Select,
  Pagination,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AirlineStopsIcon from "@mui/icons-material/AirlineStops";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import RefreshIcon from "@mui/icons-material/Refresh";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog";

import useUserRoles from "hooks/useUserRoles";

import PDFApi from "api/pdfApi";
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";
import DatosBasicosApi from "api/datosbasicosApi";

import NovedadesBandejaModal from "./novedadBandeja";
import AsignarProcesoModal from "../../components/asignarProceso";
import DesignarProcesoModal from "../../components/designarProceso";
import ActualizarAcreenciasModal from "../../components/actualizarAcreencias";

const BandejaTable = () => {
  const history = useHistory();
  const { user, roles } = useUserRoles();

  // Estados para paginación del servidor
  const [procesos, setProcesos] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);

  // Estados para filtros del servidor
  const [searchTerm, setSearchTerm] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");

  // Estados para opciones de filtros
  const [estadosDisponibles, setEstadosDisponibles] = useState([]);

  // Estados generales
  const [loading, setLoading] = useState(true);

  // Estados del menú y modales
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedProceso, setSelectedProceso] = useState(null);
  const [modalNovedadOpen, setModalNovedadOpen] = useState(false);
  const [modalAsignarOpen, setModalAsignarOpen] = useState(false);
  const [modalDesignarOpen, setModalDesignarOpen] = useState(false);
  const [modalActualizarOpen, setModalActualizarOpen] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");

  const hasFetched = useRef(false);
  const searchTimeoutRef = useRef(null);

  // FUNCIÓN PRINCIPAL PARA CARGAR DATOS - SIN DEPENDENCIAS DE ESTADO
  const fetchBandeja = useCallback(async (page, pageSize, search, estado) => {
    console.log(`🔄 [BANDEJA] Cargando datos - Página: ${page}`);
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };

      // Solo agregar parámetros no vacíos
      if (search && search.trim()) params.search = search.trim();
      if (estado) params.estado = estado;

      const response = await ProcesosApi.fetchBandejaPaginacion(params);
      setProcesos(response.data.results);
      setTotalItems(response.data.pagination.total_items);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error("Error al obtener la Bandeja:", error);
      setProcesos([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar filtros disponibles
  const cargarFiltros = useCallback(async () => {
    try {
      const estadosResponse = await DatosBasicosApi.fetchEstadosProceso();
      setEstadosDisponibles(estadosResponse.data);
    } catch (error) {
      console.error("Error al cargar filtros:", error);
    }
  }, []);

  // Effect SOLO para carga inicial
  useEffect(() => {
    if (user && !hasFetched.current) {
      console.log("🚀 [BANDEJA] Carga inicial");
      hasFetched.current = true;
      cargarFiltros();
      fetchBandeja(1, pageSize, searchTerm, estadoFiltro);
    }
  }, [user, cargarFiltros, fetchBandeja, pageSize, searchTerm, estadoFiltro]);

  // Effect SOLO para cambios de página (navegación)
  useEffect(() => {
    if (hasFetched.current) {
      console.log(`📄 [BANDEJA] Navegando a página: ${currentPage}`);
      fetchBandeja(currentPage, pageSize, searchTerm, estadoFiltro);
    }
  }, [currentPage]); // SOLO currentPage como dependencia

  // Effect para búsqueda con debounce
  useEffect(() => {
    if (!hasFetched.current) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      console.log(`🔍 [BANDEJA] Búsqueda: "${searchTerm}"`);
      setCurrentPage(1);
      fetchBandeja(1, pageSize, searchTerm, estadoFiltro);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]); // SOLO searchTerm

  // Effect para filtros
  useEffect(() => {
    if (hasFetched.current) {
      console.log("🎛️ [BANDEJA] Aplicando filtro de estado");
      setCurrentPage(1);
      fetchBandeja(1, pageSize, searchTerm, estadoFiltro);
    }
  }, [estadoFiltro]); // SOLO estadoFiltro

  // Effect para cambios de pageSize
  useEffect(() => {
    if (hasFetched.current) {
      console.log(`📊 [BANDEJA] Cambiando tamaño de página: ${pageSize}`);
      setCurrentPage(1);
      fetchBandeja(1, pageSize, searchTerm, estadoFiltro);
    }
  }, [pageSize]); // SOLO pageSize

  // Handlers para filtros del servidor
  const handleEstadoChange = (event) => {
    setEstadoFiltro(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    console.log(`👆 [BANDEJA] Usuario hace clic en página: ${newPage}`);
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const limpiarFiltros = () => {
    setSearchTerm("");
    setEstadoFiltro("");
    setCurrentPage(1);
  };

  // Función para recargar manteniendo página actual
  const reloadCurrentPage = () => {
    fetchBandeja(currentPage, pageSize, searchTerm, estadoFiltro);
  };

  // Handlers del menú (mantener todos los existentes)
  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let proceso = procesos.find((a) => a.id === id);
    setSelectedProceso(proceso);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleNovedad = (proceso) => {
    setSelectedProceso(proceso);
    setModalNovedadOpen(true);
    handleMenuClose();
  };

  const handleAsignar = (proceso) => {
    setSelectedProceso(proceso);
    setModalAsignarOpen(true);
    handleMenuClose();
  };

  const handleDesignar = (proceso) => {
    setSelectedProceso(proceso);
    setModalDesignarOpen(true);
    handleMenuClose();
  };

  const handleActualizarAcreencias = (proceso) => {
    setSelectedProceso(proceso);
    setModalActualizarOpen(true);
    handleMenuClose();
  };

  const handleAdmitirProceso = (proceso) => {
    if (proceso && proceso.id) {
      history.push(`/procesos/admitir/${proceso.identificador}?origen=bandeja`);
      handleMenuClose();
    }
  };

  const handleRechazarProceso = async (proceso) => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas Rechazar el Cargo para el Proceso ${proceso.identificador}?`
    );
    if (!confirmAction) return;
    try {
      setLoading(true);
      const FILE_NAME = "pdf_renuncia.pdf";
      const ANEXO_TYPE = "PDF_RENUNCIA";
      const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(proceso.id, {
        tipo_anexo: ANEXO_TYPE,
      });
      const anexo = anexoResponse.data;
      if (anexo != undefined && !anexoResponse.data.error) {
        await ProcesosApi.eliminarAnexoProceso(proceso.id, anexo.id);
      }
      const pdfResponse = await PDFApi.generateDocument("renunciaCargo", proceso.id);
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const formData = new FormData();
      formData.append("file", pdfBlob, FILE_NAME);
      formData.append("file_type", "proceso");
      formData.append("proceso_id", proceso.identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", ANEXO_TYPE);
      formData.append("nombre_anexo", "Renuncia Cargo");
      await BucketApi.uploadFile(formData);
      const datos = { operacion: "RECHAZAR_PROCESO", id_proceso: proceso.id };
      const response = await ProcesosApi.operarProceso(datos);
      if (response.status === 201) {
        alert("El Proceso ha sido rechazado correctamente");
        reloadCurrentPage();
      }
      handleMenuClose();
    } catch (error) {
      console.error(`Error al rechazar el Proceso:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleAceptarProceso = async (proceso) => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas Aceptar el Cargo para el Proceso ${proceso.identificador}?`
    );
    if (!confirmAction) return;
    try {
      setLoading(true);
      const FILE_NAME = "pdf_aceptacion.pdf";
      const ANEXO_TYPE = "PDF_ACEPTACION";
      const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(proceso.id, {
        tipo_anexo: ANEXO_TYPE,
      });
      const anexo = anexoResponse.data;
      if (anexo != undefined && !anexoResponse.data.error) {
        await ProcesosApi.eliminarAnexoProceso(proceso.id, anexo.id);
      }
      const pdfResponse = await PDFApi.generateDocument("aceptacionCargo", proceso.id);
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const formData = new FormData();
      formData.append("file", pdfBlob, FILE_NAME);
      formData.append("file_type", "proceso");
      formData.append("proceso_id", proceso.identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", ANEXO_TYPE);
      formData.append("nombre_anexo", "Aceptación Cargo");
      await BucketApi.uploadFile(formData);
      const datos = { operacion: "ACEPTAR_PROCESO", id_proceso: proceso.id };
      const response = await ProcesosApi.operarProceso(datos);
      if (response.status === 201) {
        alert("El Proceso ha sido aceptado correctamente");
        reloadCurrentPage();
      }
      handleMenuClose();
    } catch (error) {
      console.error(`Error al aceptar el Proceso:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerResumen = (proceso) => {
    if (proceso && proceso.id) {
      history.push(`/procesos/resumen/${proceso.identificador}?origen=bandeja`);
      handleMenuClose();
    }
  };

  const handleEdit = (proceso) => {
    if (proceso && proceso.id) {
      history.push(`/procesos/editar/${proceso.identificador}`);
      handleMenuClose();
    }
  };

  const handleViewPdf = async (proceso) => {
    const urlResponse = await BucketApi.getProcesoFileUrlFresh(
      "pdf_solicitud.pdf",
      proceso.identificador
    );
    const newUrl = urlResponse.data.file_url;
    if (urlResponse.data.exists) {
      setCurrentPdfUrl(newUrl);
      setShowPdfViewer(true);
      handleMenuClose();
      return;
    }
    alert("El PDF de Solicitud no ha sido generado.");
    handleMenuClose();
  };

  // Columnas (mantener las existentes)
  const columns = [
    {
      field: "Opciones",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)} sx={{ padding: "2px" }}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "identificador",
      headerName: "Proceso",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.identificador}
        </SuiTypography>
      ),
    },
    {
      field: "cliente_detalle",
      headerName: "Cliente",
      flex: 3,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.cliente_nombre} ({params.row.cliente_identificacion})
        </SuiTypography>
      ),
    },
    {
      field: "sede_detalle",
      headerName: "Sede",
      sortable: false,
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.sede ? params.row.sede_ciudad : "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "responsable",
      headerName: "Responsable",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.responsable_detalle
            ? params.row.responsable_detalle.usuario
              ? params.row.responsable_detalle.usuario
              : params.row.responsable_detalle.grupo
              ? "Grupo: " + params.row.responsable_detalle.grupo
              : "No tiene."
            : "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "resp_asesor",
      headerName: "Asesor",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.resp_asesor_nombre ? params.row.resp_asesor_nombre : "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "estado",
      headerName: "Estado",
      sortable: true,
      flex: 2,
      renderCell: (params) => {
        const estadoDetalle = params.row.estado_nombre || "No tiene.";
        let diasRestantes = params.row.dias_habiles_restantes;
        let prioridad;
        if (diasRestantes === undefined || diasRestantes === null) {
          prioridad = "POR DEFINIR";
        } else if (diasRestantes > 40) {
          prioridad = "BAJA";
        } else if (diasRestantes >= 20) {
          prioridad = "MEDIA";
        } else {
          prioridad = "ALTA";
        }
        const style = prioridadStyles[prioridad] || prioridadStyles["POR DEFINIR"];
        return (
          <Tooltip title={`Prioridad: ${prioridad} (${style.etiqueta})`}>
            <Box>
              <Box
                sx={{
                  display: "inline-flex",
                  backgroundColor: style.backgroundColor,
                  color: "#ffffff !important",
                  padding: "4px 12px",
                  borderRadius: "12px",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                  minWidth: "80px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {estadoDetalle}
              </Box>
              <SuiTypography
                variant="caption"
                display="block"
                fontWeight="bold"
                color={diasRestantes < 10 ? "error" : "textSecondary"}
                sx={{
                  backgroundColor: diasRestantes < 10 ? "#ffebee" : "transparent",
                  borderRadius: "4px",
                  padding: "0 4px",
                }}
              >
                {diasRestantes != null ? `${diasRestantes} días restantes` : ""}
              </SuiTypography>
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const prioridadStyles = {
    "POR DEFINIR": {
      label: "POR DEFINIR",
      etiqueta: "No Admitido",
      backgroundColor: "#808b96",
      color: "white",
    },
    BAJA: {
      label: "BAJA",
      etiqueta: "+ de 40 días",
      backgroundColor: "#43A047",
      color: "white",
    },
    MEDIA: {
      label: "MEDIA",
      etiqueta: "de 20 a 40 días",
      backgroundColor: "#f39c12",
      color: "white",
    },
    ALTA: {
      label: "ALTA",
      etiqueta: "- de 20 días",
      backgroundColor: "#E53935",
      color: "white",
    },
  };

  return (
    <SuiBox>
      {loading && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        {/* Header con controles */}
        <SuiBox p={1}>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" justifyContent="space-between" alignItems="center">
                <SuiTypography variant="h4" fontWeight="bold">
                  Bandeja de {user.nombre_completo} ({totalItems})
                </SuiTypography>
              </SuiBox>
            </Grid>
            <Grid size={6}>
              <SuiBox display="flex" gap={1} flexWrap="wrap">
                {Object.entries(prioridadStyles).map(([key, item]) => (
                  <SuiBox
                    key={key}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      padding: "2px 6px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "10px",
                        height: "10px",
                        backgroundColor: item.backgroundColor,
                        borderRadius: "4px",
                      }}
                    />
                    <SuiTypography variant="caption">{item.etiqueta}</SuiTypography>
                  </SuiBox>
                ))}
              </SuiBox>
            </Grid>
          </Grid>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 400 }}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Buscar por...
                  </SuiTypography>
                  <TextField
                    size="small"
                    placeholder="identificador y cliente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 280 }}
                  />
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Estado
                  </SuiTypography>
                  <Select value={estadoFiltro} label="Estado" onChange={handleEstadoChange}>
                    <MenuItem value="">Todos</MenuItem>
                    {estadosDisponibles.map((estado) => (
                      <MenuItem key={estado.id} value={estado.id}>
                        {estado.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Items x Página
                  </SuiTypography>
                  <Select
                    value={pageSize}
                    label="Tamaño"
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Limpiar Filtros" placement="right">
                  <IconButton
                    onClick={limpiarFiltros}
                    disabled={loading}
                    size="large"
                    sx={{ ml: 1 }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        {/* DataGrid */}
        <DataGrid
          rows={procesos}
          columns={columns}
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 1,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[100],
              },
              minHeight: "24px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "24px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />

        {/* Paginación del servidor */}
        <SuiBox display="flex" justifyContent="center" mt={2} mb={1}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
        </SuiBox>
      </Paper>

      {/* Menú contextual */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleNovedad(procesos.find((a) => a.id === selectedId))}>
          <AddCircleOutlineIcon title="NOVEDADES" fontSize="small" sx={{ mr: 1 }} /> Novedades
        </MenuItem>

        {(roles.isAdmin || roles.isOperador || roles.isCoordinador || roles.isDirector) && (
          <MenuItem onClick={() => handleVerResumen(procesos.find((a) => a.id === selectedId))}>
            <ChangeHistoryIcon fontSize="small" sx={{ mr: 1 }} /> Resumen
          </MenuItem>
        )}

        {(roles.isAdmin ||
          roles.isCoordinador ||
          roles.isAsesorExterno ||
          roles.isAsesorInterno ||
          roles.isAsistente) &&
          selectedProceso?.admitido === false && (
            <MenuItem onClick={() => handleEdit(procesos.find((a) => a.id === selectedId))}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar
            </MenuItem>
          )}

        {(roles.isAdmin || roles.isOperador || roles.isCoordinador) && (
          <MenuItem onClick={() => handleAsignar(procesos.find((a) => a.id === selectedId))}>
            <AirlineStopsIcon fontSize="small" sx={{ mr: 1 }} /> Asignar
          </MenuItem>
        )}

        {(roles.isAdmin || roles.isOperador || roles.isCoordinador || roles.isDirector) &&
          selectedProceso?.admitido === false && (
            <MenuItem onClick={() => handleDesignar(procesos.find((a) => a.id === selectedId))}>
              <ForwardToInboxIcon title="DESIGNAR" fontSize="small" sx={{ mr: 1 }} /> Designar
            </MenuItem>
          )}

        {(roles.isAdmin || roles.isOperador || roles.isCoordinador) &&
          selectedProceso?.admitido === false && (
            <MenuItem
              onClick={() => handleAceptarProceso(procesos.find((a) => a.id === selectedId))}
            >
              <ThumbUpAltIcon title="ACEPTAR" fontSize="small" sx={{ mr: 1 }} /> Aceptar
            </MenuItem>
          )}

        {(roles.isAdmin || roles.isOperador || roles.isCoordinador) &&
          selectedProceso?.admitido === false && (
            <MenuItem
              onClick={() => handleRechazarProceso(procesos.find((a) => a.id === selectedId))}
            >
              <HighlightOffIcon title="RECHAZAR" fontSize="small" sx={{ mr: 1 }} /> Rechazar
            </MenuItem>
          )}

        {(roles.isAdmin || roles.isOperador || roles.isCoordinador) &&
          selectedProceso?.admitido === false && (
            <MenuItem
              onClick={() => handleAdmitirProceso(procesos.find((a) => a.id === selectedId))}
            >
              <DoneAllIcon title="ADMITIR" fontSize="small" sx={{ mr: 1 }} /> Admitir
            </MenuItem>
          )}

        {(roles.isAdmin || roles.isOperador || roles.isCoordinador) && (
          <MenuItem
            onClick={() => handleActualizarAcreencias(procesos.find((a) => a.id === selectedId))}
          >
            <UpgradeIcon fontSize="small" sx={{ mr: 1 }} /> Actualizar Acreencias
          </MenuItem>
        )}

        <MenuItem onClick={() => handleViewPdf(procesos.find((a) => a.id === selectedId))}>
          <PictureAsPdfIcon title="PDF SOLICITUD" fontSize="small" sx={{ mr: 1 }} /> Ver PDF
          Solicitud
        </MenuItem>
      </Menu>

      {/* Modales */}
      <NovedadesBandejaModal
        open={modalNovedadOpen}
        onClose={() => setModalNovedadOpen(false)}
        proceso={selectedProceso}
        onSuccess={() => {
          reloadCurrentPage();
          setModalNovedadOpen(false);
        }}
      />
      <AsignarProcesoModal
        open={modalAsignarOpen}
        onClose={() => setModalAsignarOpen(false)}
        proceso={selectedProceso}
        onSuccess={() => {
          reloadCurrentPage();
          setModalAsignarOpen(false);
        }}
      />
      <DesignarProcesoModal
        open={modalDesignarOpen}
        onClose={() => setModalDesignarOpen(false)}
        proceso={selectedProceso}
        onSuccess={() => {
          reloadCurrentPage();
          setModalDesignarOpen(false);
        }}
      />
      <ActualizarAcreenciasModal
        open={modalActualizarOpen}
        onClose={() => setModalActualizarOpen(false)}
        proceso={selectedProceso}
        onSuccess={() => {
          reloadCurrentPage();
          setModalActualizarOpen(false);
        }}
      />
      <FileDialog
        open={showPdfViewer}
        onClose={() => setShowPdfViewer(false)}
        title="Vista previa del PDF"
        content={
          <embed
            src={currentPdfUrl}
            type="application/pdf"
            width="100%"
            height="500px"
            style={{ border: "none" }}
          />
        }
      />
    </SuiBox>
  );
};

export default BandejaTable;
